import * as XLSX from "xlsx";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Dropdown, Option } from "@fluentui/react-components";
import { Spinner, SpinnerSize } from "@fluentui/react";

import Pagination from "../../components/pagination/Pagination";
import TableComponent from "../../components/table/TableComponent";
import { TABLE } from "../../utils/Enums";
import { AdminApprenticeWizardsService } from "../../KulcsUzletSvc/services/AdminApprenticeWizardsService";
import { ApprenticeWizardRow } from "../../components/table/TableApprenticeWizardsColumns";
import { useStylesMainPage } from "../../styles/MainPageStyle";

export default function ApprenticeWizardsPage() {
  const styles = useStylesMainPage();
  const navigate = useNavigate();

  const [data, setData] = useState<ApprenticeWizardRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const navigateBack = () => {
    navigate(-1);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const skipValue = (currentPage - 1) * pageSize;
      const res = await AdminApprenticeWizardsService.getAdminApprenticeWizards(
        undefined,
        skipValue,
        pageSize,
        undefined,
      );

      const items = res.items ?? [];
      setTotalCount(res.totalCount ?? 0);

      const rows: ApprenticeWizardRow[] = items.map((item, idx) => ({
        rowKey: `${item.taxNumber ?? ""}-${item.email ?? ""}-${skipValue + idx}`,
        companyName: item.companyName,
        contactName: item.contactName,
        email: item.email,
        phone: item.phone,
        taxNumber: item.taxNumber,
        status: item.status,
      }));

      setData(rows);
    } catch (e) {
      console.error("Adatlekérési hiba:", e);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const exportList = () => {
    try {
      if (data.length === 0) {
        alert("Nincsenek exportálandó adatok az aktuális oldalon");
        return;
      }

      const headers = [
        "Cégnév",
        "Kapcsolattartó",
        "Email",
        "Telefon",
        "Adószám",
        "Wizard oldal",
      ];

      const rows = data.map((item) => [
        item.companyName ?? "",
        item.contactName ?? "",
        item.email ?? "",
        item.phone ?? "",
        item.taxNumber ?? "",
        item.status ?? "",
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Bevallas");

      const fileName = `bevallas_page_${currentPage}_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error("Export failed", e);
    }
  };

  return (
    <div className={styles.main}>
      <div
        style={{
          textAlign: "center",
          width: "100%",
          fontWeight: "bold",
          fontSize: "40px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Bevallás
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Button onClick={navigateBack}>Vissza</Button>
          <Dropdown
            value={String(pageSize)}
            style={{ minWidth: "80px" }}
            onOptionSelect={(_, option) => {
              setPageSize(Number(option.optionValue));
              setCurrentPage(1);
            }}
          >
            <Option value="10">10</Option>
            <Option value="20">20</Option>
            <Option value="50">50</Option>
            <Option value="100">100</Option>
          </Dropdown>
          <span style={{ fontSize: "14px" }}>találat oldalaként</span>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <Button onClick={exportList}>Excel</Button>
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spinner size={SpinnerSize.large} label="Betöltés..." />
        </div>
      ) : (
        <>
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={(p) => setCurrentPage(p)}
          />

          <TableComponent
            type={TABLE.APPRENTICE_WIZARDS}
            data={data}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
}
